import { useState } from "react";
import arrowIcon from "@assets/icons/arrow-up-s-fill.svg";

interface Benefit {
  id: string;
  title: string;
  header?: string;
  item: string[];
  footer?: string;
}

interface BenefitsAccordionProps {
  benefits: Benefit[];
}

export default function BenefitsAccordion({
  benefits,
}: BenefitsAccordionProps) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const toggleAccordion = (index: number) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="mx-auto w-full  mt-6 md:mt-8 lg:mt-12 xl:mt-16 flex flex-col gap-3 md:gap-4 px-4 md:px-6 lg:px-8">
      {benefits.map((benefit, index) => {
        const isOpen = openItems.has(index);
        return (
          <div
            key={benefit.id}
            className="w-full max-w-[1368px] mx-auto bg-white rounded-[16px] md:rounded-[20px] lg:rounded-[24px] overflow-hidden transition-all duration-500 ease-in-out shadow-sm"
          >
            {/* Accordion Header */}
            <button
              onClick={() => toggleAccordion(index)}
              className="w-full flex items-center justify-between px-4 md:px-6 lg:px-[24px] py-4 md:py-5 lg:py-[24px] border-b border-[#B9BBC6] hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center gap-2 md:gap-3 lg:gap-[12px]">
                {/* id */}
                <span className="size-[48px] md:size-[48px] lg:size-[56px] flex items-center justify-center text-[18px] md:text-[20px] lg:text-[24px] font-semibold text-white bg-black rounded-[8px] md:rounded-[10px] lg:rounded-[12px] flex-shrink-0">
                  {benefit.id}
                </span>

                {/* title */}
                <p className="text-[18px] md:text-[20px] lg:text-[24px] font-medium leading-tight md:leading-normal text-[#090D2C] text-left">
                  {benefit.title}
                </p>
              </div>
              <img
                src={arrowIcon}
                alt=""
                className={`transition-transform duration-500 ease-in-out w-6 h-6 md:w-6 md:h-6 lg:w-7 lg:h-7 flex-shrink-0 ml-2 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>

            {/* Accordion Content */}
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                isOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-4 md:px-8 lg:px-[80px] text-[#62636C] text-[18px] md:text-[20px] lg:text-[24px] font-medium py-4 md:py-5 lg:py-[25px]">
                {/* header */}
                {benefit.header && (
                  <h1 className="mb-3 md:mb-[12px] text-[18px] md:text-[20px] lg:text-[24px]">
                    {benefit.header}
                  </h1>
                )}

                {/* items */}
                {benefit.item && benefit.item.length > 0 && (
                  <div
                    className={`flex flex-col ${benefit.item.length > 1 ? "gap-3 md:gap-4 lg:gap-[24px]" : ""}`}
                  >
                    {benefit.item.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 md:gap-3">
                        {benefit.item.length > 1 && (
                          <span className="min-w-[10px] min-h-[10px] md:min-w-[10px] md:min-h-[10px] lg:min-w-[12px] lg:min-h-[12px] rounded-full bg-[#62636C] mt-1.5 md:mt-2 flex-shrink-0"></span>
                        )}
                        <h1 className="text-[16px] md:text-[16px] lg:text-[18px] text-[#0A0E2EBF] font-normal leading-relaxed lg:leading-[26px]">
                          {item}
                        </h1>
                      </div>
                    ))}
                  </div>
                )}

                {/* footer */}
                {benefit.footer && (
                  <h1 className="text-[18px] md:text-[20px] lg:text-[24px] mt-3 md:mt-[12px] text-[#62636C] font-normal">
                    {benefit.footer}
                  </h1>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
